import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAppSelector, useAppDispatch } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, RotateCcw } from "lucide-react";
import { reachedTerminal, stepChanged, bookingConfirmed } from "../publicBookingFlowSlice";
import { useCreateBookingMutation } from "../publicBookingFlowApi";
import { buildCreateBookingPayload } from "../buildCreateBookingPayload";
import { classifyCreateBookingFailure } from "../createBookingFailure";
import { HELD_TIME_GONE_TERMINAL, SLOT_TAKEN_TERMINAL } from "../publicBookingFlowTypes";
import { CalThrottledNotice } from "@/components/CalThrottledNotice";

export const AppointmentBookingStep = () => {
  const dispatch = useAppDispatch();
  const customerData = useAppSelector(state => state.publicBookingFlow.customerData);
  const householdData = useAppSelector(state => state.publicBookingFlow.householdData);
  const slotData = useAppSelector(state => state.publicBookingFlow.targetSlot);

  const [createBooking] = useCreateBookingMutation();
  const [status, setStatus] = useState<"assigning" | "retrying" | "failed">("assigning");
  const [errorMessage, setErrorMessage] = useState("");
  // The scheduling service was busy (AB#514). Kept while the retry is in flight, so the notice — and
  // the one automatic retry it has already spent — stays put rather than starting over.
  const [throttled, setThrottled] = useState<unknown>(null);
  const hasAttemptedRef = useRef(false);
  // At most one automatic resend of the create, whichever path spends it (AB#514): the 5xx retry
  // below or the busy notice's own retry when its countdown ends. The ref is read inside the async
  // attempt; the state mirrors it for the notice's autoRetry prop.
  const autoRetrySpentRef = useRef(false);
  const [autoRetrySpent, setAutoRetrySpent] = useState(false);
  const spendAutoRetry = useCallback(() => {
    autoRetrySpentRef.current = true;
    setAutoRetrySpent(true);
  }, []);

  const attempt = useCallback(async (isRetry: boolean) => {
    setStatus(isRetry ? "retrying" : "assigning");

    // Never a create without a hold: the hold is what the server books, and it is the idempotency
    // key that makes the retry below safe.
    if (!customerData || !householdData || !slotData?.reservationUid) {
      console.error("Required data is missing for booking.", { hasCustomer: !!customerData, hasHousehold: !!householdData, hasReservation: !!slotData?.reservationUid });
      dispatch(reachedTerminal({ type: "missing_data", detail: { message: "Some required information is missing. Please start over." } }));
      return;
    }

    try {
      const booking = await createBooking(buildCreateBookingPayload(customerData, householdData, slotData)).unwrap();
      setThrottled(null);
      dispatch(bookingConfirmed(booking));
      dispatch(stepChanged(6));
    } catch (err) {
      const failure = classifyCreateBookingFailure(err);
      if (failure.kind !== "throttled") {
        setThrottled(null);
      }
      switch (failure.kind) {
        case "seasonClosed":
          dispatch(reachedTerminal(failure.terminal));
          return;

        // Refusals are terminal answers, not failures, and must not be reported as one.
        //
        // slot_taken: no appointment was made and the hold is gone, so the terminal offers
        // "Find another time", the same as held_time_gone below.
        case "slotTaken":
          dispatch(reachedTerminal({ type: SLOT_TAKEN_TERMINAL }));
          return;

        // program_refused (M9): no funding program remains for this household. Only Start Over.
        case "programRefused":
          dispatch(reachedTerminal(failure.terminal));
          return;

        // No noticeId and no phoneLast4 — this response carries neither, because the number on the
        // existing appointment is not in it. The terminal renders its "call us" form, which is the
        // right thing here: somebody who reached this step already answered the step-2 check, so if
        // we could offer them the call we would have offered it there.
        case "householdAlreadyBooked":
          dispatch(reachedTerminal({ type: "hh_existing_appointment" }));
          return;

        // AB#632: the hold this booking was made against is gone (expired, released, or its time
        // taken). Try Again would resubmit the same dead hold and fail with ReservationNotFound; the
        // terminal offers "Find another time" instead, which gets a new hold for the saved ZIP and
        // comes back here with the same answers.
        case "heldTimeGone":
          dispatch(reachedTerminal({ type: HELD_TIME_GONE_TERMINAL }));
          return;

        // Safe to send again for the same reason the retryable case is: the hold is the idempotency key.
        case "throttled":
          setThrottled(failure.error);
          setStatus("failed");
          return;

        // First failure → auto-retry once, but only for transient (network/5xx) errors, on the same
        // hold, and only if no automatic resend has been spent already.
        case "retryable":
          if (!isRetry && !autoRetrySpentRef.current) {
            spendAutoRetry();
            await attempt(true);
            return;
          }
          setErrorMessage(failure.message);
          setStatus("failed");
          return;

        case "error":
          setErrorMessage(failure.message);
          setStatus("failed");
          return;
      }
    }
  }, [customerData, householdData, slotData, createBooking, dispatch, spendAutoRetry]);

  useEffect(() => {
    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;
    attempt(false);
  }, [attempt]);

  if (throttled != null) {
    return (
      <div className="w-full max-w-xl mx-auto py-10">
        <CalThrottledNotice
          variant="public"
          error={throttled}
          onRetry={() => {
            // The notice's automatic retry and its button both come through here; either way no
            // further automatic resend is owed.
            spendAutoRetry();
            attempt(true);
          }}
          busy={status === "retrying"}
          autoRetry={!autoRetrySpent}
        />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm text-center py-10">
        <CardHeader>
          <div className="mx-auto w-14 h-14 bg-red-100 flex items-center justify-center rounded-full mb-2">
            <AlertCircle className="w-7 h-7 text-red-600" />
          </div>
          <CardTitle className="text-xl font-semibold text-slate-900">Unable to Schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-600 text-sm">{errorMessage}</p>
          <p className="text-xs text-slate-400">
            If this issue persists, try again tomorrow.
          </p>
          <Button variant="outline" className="gap-2" onClick={() => attempt(true)}>
            <RotateCcw className="w-4 h-4" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm text-center py-12">
      <CardHeader>
        <CardTitle className="text-2xl font-semibold text-slate-900">
          {status === "retrying" ? "Retrying…" : "Finding Your Appointment…"}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: "#009681" }} />
        <p className="text-slate-500 max-w-sm text-sm">
          The system is assigning the first available slot and an intake staff member.
          Please do not refresh this page.
        </p>
      </CardContent>
    </Card>
  );
}
